/**
 * @namespace frameTimer
 * @desc Ermöglicht das flexible Anhängen von Events an die requestAnimationFrame-API.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame
 */

const {window, Date} = globalThis;
const handlers = [];

const requestFrame =
    window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    window.msRequestAnimationFrame;

const cancelFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame;

let lastId = 0;
let active = false;
let lastRequestId;
let timestamp;

const processHandler = function(handler)
{
    if (handler.beforeFrames < handler.after)
    {
        handler.beforeFrames++;
        return;
    }

    handler.everyCounter++;
    if (handler.everyCounter !== handler.every)
    {
        return;
    }
    handler.everyCounter = 0;

    if (handler.repeat >= 0)
    {
        if (handler.repeats === handler.repeat)
        {
            removeHandler(handler); // eslint-disable-line
        }
        handler.repeats++;
    }

    if (handler.scope !== null)
    {
        handler.callback.call(handler.scope, timestamp);
    }
    else { handler.callback(timestamp); }
};

const time = function()
{
    lastRequestId = requestFrame(time);
    timestamp = Date.now();
    handlers.forEach(processHandler);
};

const startTimer = function()
{
    if (active) { return; }
    lastRequestId = requestFrame(time);
    active = true;
};

const stopTimer = function()
{
    if (!active) { return; }
    cancelFrame(lastRequestId);
    active = false;
};

const addHandler = function(handler)
{
    handlers.push(handler);
    startTimer();
};

const removeHandler = function(handler)
{
    if (handler)
    {
        handlers.splice(handlers.indexOf(handler), 1);
        if (typeof handler.removeCallback === 'function')
        {
            handler.removeCallback();
        }
    }

    if (handlers.length === 0)
    {
        stopTimer();
    }
};

const createHandler = function(options)
{
    lastId++;
    return {
        after: 0,
        repeat: -1,
        every: 1,
        scope: null,
        ...options,
        beforeFrames: 0,
        repeats: 0,
        everyCounter: 0,
        id: lastId
    };
};

/**
 * @typedef {Object} frameTimer~TimerOptions
 * @property {Function} callback
 * @property {Object} [scope=null]
 * @property {number} [after=0]
 * @property {number} [repeat=-1]
 * @property {number} [every=1]
 * @property {Function} [removeCallback] Wird aufgerufen beim manuellen Cancel oder wenn der Timer beendet wird.
 */

const frameTimer = /** @lends frameTimer */
{
    /**
     * @param {Function} callback
     * @param {Object} [scope=null]
     * @return {number} Die Id zum Abbrechen des Callbacks.
     */
    always(callback, scope = null)
    {
        return this.add({callback, scope});
    },

    /**
     * @param {number} every Callback wird nur alle ``every`` Frames ausgeführt.
     * @param {Function} callback
     * @param {Object} [scope=null]
     * @return {number} Die Id zum Abbrechen des Callbacks.
     */
    every(every, callback, scope = null)
    {
        return this.add({
            callback, every, scope, after: 0, repeat: -1
        });
    },

    /** Callback wird zum nächsten Frame einmal ausgeführt.
     * @param {Function} callback
     * @param {Object} [scope=null]
     * @return {number} Die Id zum Abbrechen des Callbacks.
     */
    next(callback, scope = null)
    {
        return this.add({
            callback, scope, after: 0, repeat: 0
        });
    },

    /** Callback warte die übergebene Anzahl von Frames, bevor es einmal ausgeführt wird. ``0`` entspricht der next-Methode.
     * @param {number} frames
     * @param {Function} callback
     * @param {Object} [scope=null]
     * @return {number} Die Id zum Abbrechen des Callbacks.
     */
    after(frames, callback, scope = null)
    {
        return this.add({
            callback, scope, after: frames, repeat: 0
        });
    },

    /** Erlaubt diverse Kombination der Timer-Optionen.
     * @param {frameTimer~TimerOptions} options
     * @return {number} Die Id zum Abbrechen des Callbacks.
     */
    add(options)
    {
        const handler = createHandler(options);
        addHandler(handler);
        return handler.id;
    },

    /** @param {number} id */
    cancel(id)
    {
        removeHandler(handlers.find(handler => (handler.id === id)));
    }
};

export default frameTimer;
